import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { cop, num, pct } from '../lib/format';
import type { PuntoEquilibrio } from '../api/types';
import { PageHeader } from '../components/PageHeader';
import {
  derivarEquilibrio,
  margenSeguridadPct,
  SEMANAS_POR_MES,
  DIAS_POR_MES,
  type EquilibrioDerivado,
} from '../lib/punto-equilibrio';

export function PuntoEquilibrioPage() {
  const { tiene } = useAuth();
  const peQ = useQuery({
    queryKey: ['punto-equilibrio'],
    queryFn: () => API.get<PuntoEquilibrio>('/reportes/punto-equilibrio'),
    enabled: tiene('reportes:read'),
  });

  if (!tiene('reportes:read')) {
    return (
      <div className="space-y-4">
        <PageHeader title="Punto de equilibrio" />
        <div className="card p-4 text-sm text-slate-500">
          No tienes permiso para ver este reporte.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title="Punto de equilibrio"
        subtitle="Cuántos paquetes necesitas vender para cubrir todos tus costos fijos."
      />

      {peQ.isLoading && (
        <div className="card p-4 text-sm text-slate-500">Cargando…</div>
      )}

      {peQ.isError && (
        <div className="card p-4 text-sm text-red-600">
          No fue posible cargar los datos del punto de equilibrio.
        </div>
      )}

      {peQ.data && <PuntoEquilibrioContenido pe={peQ.data} />}
    </div>
  );
}

function PuntoEquilibrioContenido({ pe }: { pe: PuntoEquilibrio }) {
  const [precio, setPrecio] = useState(pe.precioPromedio);
  const [costoVar, setCostoVar] = useState(pe.costoVariablePromedio);
  const [fijos, setFijos] = useState(pe.costosFijosMensuales);
  const [paquetesMes, setPaquetesMes] = useState(pe.paquetesEstimadosMes);

  const real = useMemo(
    () =>
      derivarEquilibrio({
        precio: pe.precioPromedio,
        costoVariable: pe.costoVariablePromedio,
        costosFijosMensuales: pe.costosFijosMensuales,
      }),
    [pe],
  );

  const sim = useMemo(
    () =>
      derivarEquilibrio({
        precio,
        costoVariable: costoVar,
        costosFijosMensuales: fijos,
      }),
    [precio, costoVar, fijos],
  );

  const tocado =
    precio !== pe.precioPromedio ||
    costoVar !== pe.costoVariablePromedio ||
    fijos !== pe.costosFijosMensuales ||
    paquetesMes !== pe.paquetesEstimadosMes;

  const restablecer = () => {
    setPrecio(pe.precioPromedio);
    setCostoVar(pe.costoVariablePromedio);
    setFijos(pe.costosFijosMensuales);
    setPaquetesMes(pe.paquetesEstimadosMes);
  };

  return (
    <>
      <ResumenEjecutivo pe={pe} sim={sim} paquetesMes={paquetesMes} tocado={tocado} />

      <EquilibrioPorUnidad sim={sim} paquetesMes={paquetesMes} />

      <BreakEvenChart
        precio={precio}
        costoVariable={costoVar}
        fijos={fijos}
        equilibrioMes={sim.paquetesEquilibrioMes}
        estimadoMes={paquetesMes}
      />

      <Simulador
        pe={pe}
        precio={precio}
        costoVar={costoVar}
        fijos={fijos}
        paquetesMes={paquetesMes}
        sim={sim}
        real={real}
        tocado={tocado}
        onPrecio={setPrecio}
        onCostoVar={setCostoVar}
        onFijos={setFijos}
        onPaquetesMes={setPaquetesMes}
        onReset={restablecer}
      />
    </>
  );
}

function ResumenEjecutivo({
  pe,
  sim,
  paquetesMes,
  tocado,
}: {
  pe: PuntoEquilibrio;
  sim: EquilibrioDerivado;
  paquetesMes: number;
  tocado: boolean;
}) {
  const margen = margenSeguridadPct(paquetesMes, sim.paquetesEquilibrioMes);
  const imposible = sim.paquetesEquilibrioMes === null;

  return (
    <div className="card p-3 sm:p-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 mb-3">
        <h2 className="font-semibold">Resumen</h2>
        {tocado && (
          <span className="text-xs text-amber-600">
            Mostrando valores simulados (no afecta tus datos).
          </span>
        )}
      </div>

      {imposible ? (
        <p className="text-sm text-red-600">
          El precio de venta no cubre el costo variable. Sube el precio o baja el costo
          en el simulador para encontrar un punto de equilibrio.
        </p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat
            label="Paquetes/mes para empatar"
            value={num(sim.paquetesEquilibrioMes ?? 0, 0)}
            strong
          />
          <Stat
            label="Aporte por paquete"
            value={cop(sim.contribucionPaq)}
            hint={`${pct(sim.contribucionPct)} del precio queda para cubrir fijos`}
          />
          <Stat
            label="Costos fijos mensuales"
            value={cop(pe.costosFijosMensuales)}
            hint={`Depreciación ${cop(pe.depreciacionMensual)} + otros ${cop(pe.otrosFijosMensuales)}`}
          />
          <Stat
            label="Margen de seguridad"
            value={margen === null ? '—' : pct(margen)}
            hint={
              margen === null
                ? 'Sin equilibrio definido'
                : margen >= 0
                  ? `Vendiendo ${num(paquetesMes, 0)} paq./mes estás por encima`
                  : `Vendiendo ${num(paquetesMes, 0)} paq./mes estás por debajo`
            }
            color={margen === null ? undefined : margen >= 0 ? 'text-emerald-700' : 'text-red-600'}
          />
        </div>
      )}
    </div>
  );
}

function EquilibrioPorUnidad({
  sim,
  paquetesMes,
}: {
  sim: EquilibrioDerivado;
  paquetesMes: number;
}) {
  if (sim.paquetesEquilibrioMes === null) return null;

  const estimadoSemana = paquetesMes / SEMANAS_POR_MES;
  const estimadoDia = paquetesMes / DIAS_POR_MES;

  return (
    <div className="card p-3 sm:p-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 mb-3">
        <h2 className="font-semibold">Equilibrio por unidad de tiempo</h2>
        <span className="text-xs text-slate-500">
          Aproximaciones con {SEMANAS_POR_MES} semanas y {DIAS_POR_MES} días por mes.
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <UnidadCard
          label="Por mes"
          equilibrio={sim.paquetesEquilibrioMes}
          estimado={paquetesMes}
        />
        <UnidadCard
          label="Por semana"
          equilibrio={sim.paquetesEquilibrioSemana ?? 0}
          estimado={estimadoSemana}
        />
        <UnidadCard
          label="Por día"
          equilibrio={sim.paquetesEquilibrioDia ?? 0}
          estimado={estimadoDia}
        />
      </div>
    </div>
  );
}

function UnidadCard({
  label,
  equilibrio,
  estimado,
}: {
  label: string;
  equilibrio: number;
  estimado: number;
}) {
  const cubre = estimado >= equilibrio;
  const dif = Math.abs(estimado - equilibrio);
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-xl sm:text-2xl font-semibold mt-1">{num(equilibrio, 0)}</div>
      <div className={`text-[11px] mt-1 ${cubre ? 'text-emerald-700' : 'text-red-600'}`}>
        {cubre
          ? `Vendes ~${num(estimado, 0)} (sobran ${num(dif, 0)})`
          : `Vendes ~${num(estimado, 0)} (faltan ${num(dif, 0)})`}
      </div>
    </div>
  );
}

function BreakEvenChart({
  precio,
  costoVariable,
  fijos,
  equilibrioMes,
  estimadoMes,
}: {
  precio: number;
  costoVariable: number;
  fijos: number;
  equilibrioMes: number | null;
  estimadoMes: number;
}) {
  const W = 600;
  const H = 320;
  const padL = 56;
  const padR = 16;
  const padT = 16;
  const padB = 36;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  if (equilibrioMes === null) {
    return (
      <div className="card p-3 sm:p-4">
        <h2 className="font-semibold mb-2">Gráfico de equilibrio</h2>
        <p className="text-sm text-slate-500">
          No se puede graficar: con estos valores los costos siempre superan los ingresos.
        </p>
      </div>
    );
  }

  const xMax = Math.max(
    Math.ceil(equilibrioMes * 1.5),
    Math.ceil(estimadoMes * 1.2),
    1,
  );
  const yMax = Math.max(precio * xMax, fijos + costoVariable * xMax, 1);

  const xToPx = (x: number) => padL + (x / xMax) * innerW;
  const yToPx = (y: number) => padT + innerH - (y / yMax) * innerH;

  const ingresoFin = { x: xToPx(xMax), y: yToPx(precio * xMax) };
  const costoIni = { x: xToPx(0), y: yToPx(fijos) };
  const costoFin = { x: xToPx(xMax), y: yToPx(fijos + costoVariable * xMax) };
  const cruce = { x: xToPx(equilibrioMes), y: yToPx(precio * equilibrioMes) };

  const perdidaPath = [
    `M ${xToPx(0)} ${yToPx(0)}`,
    `L ${cruce.x} ${cruce.y}`,
    `L ${costoIni.x} ${costoIni.y}`,
    `Z`,
  ].join(' ');

  const gananciaPath = [
    `M ${cruce.x} ${cruce.y}`,
    `L ${ingresoFin.x} ${ingresoFin.y}`,
    `L ${costoFin.x} ${costoFin.y}`,
    `Z`,
  ].join(' ');

  const yTicks = 4;
  const xTicks = 5;

  return (
    <div className="card p-3 sm:p-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 mb-2">
        <h2 className="font-semibold">Gráfico de equilibrio</h2>
        <span className="text-xs text-slate-500">
          Donde la línea verde cruza la roja, no ganas ni pierdes.
        </span>
      </div>
      <div className="w-full overflow-x-auto">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-auto min-w-[480px]"
          role="img"
          aria-label="Gráfico de punto de equilibrio"
        >
          <rect x={padL} y={padT} width={innerW} height={innerH} fill="white" />

          <path d={perdidaPath} fill="rgba(239,68,68,0.12)" />
          <path d={gananciaPath} fill="rgba(16,185,129,0.15)" />

          {Array.from({ length: yTicks + 1 }).map((_, i) => {
            const v = (yMax * i) / yTicks;
            const yPx = yToPx(v);
            return (
              <g key={`y${i}`}>
                <line
                  x1={padL}
                  x2={padL + innerW}
                  y1={yPx}
                  y2={yPx}
                  stroke="#e2e8f0"
                  strokeDasharray="2 3"
                />
                <text
                  x={padL - 6}
                  y={yPx + 4}
                  textAnchor="end"
                  fontSize="10"
                  fill="#64748b"
                >
                  {cop(v)}
                </text>
              </g>
            );
          })}

          {Array.from({ length: xTicks + 1 }).map((_, i) => {
            const v = (xMax * i) / xTicks;
            const xPx = xToPx(v);
            return (
              <g key={`x${i}`}>
                <line
                  x1={xPx}
                  x2={xPx}
                  y1={padT + innerH}
                  y2={padT + innerH + 4}
                  stroke="#94a3b8"
                />
                <text
                  x={xPx}
                  y={padT + innerH + 16}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#64748b"
                >
                  {num(v, 0)}
                </text>
              </g>
            );
          })}

          <line
            x1={padL}
            x2={padL}
            y1={padT}
            y2={padT + innerH}
            stroke="#94a3b8"
          />
          <line
            x1={padL}
            x2={padL + innerW}
            y1={padT + innerH}
            y2={padT + innerH}
            stroke="#94a3b8"
          />

          <line
            x1={xToPx(0)}
            y1={yToPx(0)}
            x2={ingresoFin.x}
            y2={ingresoFin.y}
            stroke="#10b981"
            strokeWidth={2}
          />
          <line
            x1={costoIni.x}
            y1={costoIni.y}
            x2={costoFin.x}
            y2={costoFin.y}
            stroke="#ef4444"
            strokeWidth={2}
          />

          {estimadoMes > 0 && estimadoMes <= xMax && (
            <g>
              <line
                x1={xToPx(estimadoMes)}
                x2={xToPx(estimadoMes)}
                y1={padT}
                y2={padT + innerH}
                stroke="#0ea5e9"
                strokeDasharray="4 3"
              />
              <text
                x={xToPx(estimadoMes)}
                y={padT + 12}
                textAnchor="middle"
                fontSize="10"
                fill="#0369a1"
              >
                Estimado: {num(estimadoMes, 0)}
              </text>
            </g>
          )}

          <circle cx={cruce.x} cy={cruce.y} r={5} fill="#0f172a" />
          <text
            x={cruce.x + 8}
            y={cruce.y - 8}
            fontSize="11"
            fill="#0f172a"
            fontWeight="600"
          >
            {num(equilibrioMes, 0)} paq · {cop(precio * equilibrioMes)}
          </text>

          <text
            x={padL + innerW / 2}
            y={H - 4}
            textAnchor="middle"
            fontSize="10"
            fill="#475569"
          >
            Paquetes vendidos al mes
          </text>
        </svg>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 mt-2">
        <LegendDot color="#10b981" label="Ingresos" />
        <LegendDot color="#ef4444" label="Costos totales (fijos + variables)" />
        <LegendDot color="#0ea5e9" label="Tu nivel estimado" />
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block w-3 h-0.5" style={{ background: color }} />
      {label}
    </span>
  );
}

function Simulador({
  pe,
  precio,
  costoVar,
  fijos,
  paquetesMes,
  sim,
  real,
  tocado,
  onPrecio,
  onCostoVar,
  onFijos,
  onPaquetesMes,
  onReset,
}: {
  pe: PuntoEquilibrio;
  precio: number;
  costoVar: number;
  fijos: number;
  paquetesMes: number;
  sim: EquilibrioDerivado;
  real: EquilibrioDerivado;
  tocado: boolean;
  onPrecio: (n: number) => void;
  onCostoVar: (n: number) => void;
  onFijos: (n: number) => void;
  onPaquetesMes: (n: number) => void;
  onReset: () => void;
}) {
  const deltaPaquetes =
    sim.paquetesEquilibrioMes !== null && real.paquetesEquilibrioMes !== null
      ? sim.paquetesEquilibrioMes - real.paquetesEquilibrioMes
      : null;

  const utilidadSim = (precio - costoVar) * paquetesMes - fijos;
  const cubreEquilibrio =
    sim.paquetesEquilibrioMes !== null && paquetesMes >= sim.paquetesEquilibrioMes;

  const baseSemana = pe.paquetesEstimadosMes / SEMANAS_POR_MES;
  const baseDia = pe.paquetesEstimadosMes / DIAS_POR_MES;

  return (
    <div className="card p-3 sm:p-4 space-y-5">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
        <div>
          <h2 className="font-semibold">Simulador "¿qué pasa si…?"</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Mueve los controles para explorar escenarios. No modifica tus datos.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary text-xs"
          onClick={onReset}
          disabled={!tocado}
        >
          Restablecer
        </button>
      </div>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
          Precios y costos
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SliderCampo
            label="Precio promedio por paquete"
            valor={precio}
            base={pe.precioPromedio}
            rango={0.5}
            formato="cop"
            onChange={onPrecio}
          />
          <SliderCampo
            label="Costo variable por paquete"
            valor={costoVar}
            base={pe.costoVariablePromedio}
            rango={0.5}
            formato="cop"
            onChange={onCostoVar}
          />
          <SliderCampo
            label="Costos fijos mensuales"
            valor={fijos}
            base={pe.costosFijosMensuales}
            rango={0.5}
            formato="cop"
            maxAbsoluto={2_000_000}
            onChange={onFijos}
          />
        </div>
      </div>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
          Volumen de venta
        </h3>
        <p className="text-[11px] text-slate-500 mb-2">
          Los tres deslizadores están sincronizados — mover uno actualiza los otros.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SliderCampo
            label="Paquetes por día"
            valor={paquetesMes / DIAS_POR_MES}
            base={baseDia}
            rango={1}
            formato="num"
            onChange={(n) => onPaquetesMes(Math.max(0, n) * DIAS_POR_MES)}
          />
          <SliderCampo
            label="Paquetes por semana"
            valor={paquetesMes / SEMANAS_POR_MES}
            base={baseSemana}
            rango={1}
            formato="num"
            onChange={(n) => onPaquetesMes(Math.max(0, n) * SEMANAS_POR_MES)}
          />
          <SliderCampo
            label="Paquetes por mes"
            valor={paquetesMes}
            base={pe.paquetesEstimadosMes}
            rango={1}
            formato="num"
            onChange={onPaquetesMes}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {deltaPaquetes !== null && (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Con estos cambios necesitas{' '}
            <b className="text-slate-800">
              {deltaPaquetes === 0
                ? 'los mismos paquetes'
                : deltaPaquetes < 0
                  ? `${num(Math.abs(deltaPaquetes), 0)} paquetes menos`
                  : `${num(deltaPaquetes, 0)} paquetes más`}
            </b>{' '}
            al mes para empatar (vs. el real de {num(real.paquetesEquilibrioMes ?? 0, 0)}).
          </div>
        )}
        <div
          className={`rounded-md border px-3 py-2 text-xs ${
            cubreEquilibrio
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
        >
          Vendiendo <b>{num(paquetesMes, 0)}</b> paq./mes la utilidad mensual proyectada es{' '}
          <b>{cop(utilidadSim)}</b>{' '}
          {cubreEquilibrio ? '(cubres los costos fijos).' : '(aún no cubres los costos fijos).'}
        </div>
      </div>

      {sim.paquetesEquilibrioMes === null && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          Con estos valores el costo variable supera el precio: no existe punto de
          equilibrio.
        </div>
      )}
    </div>
  );
}

function SliderCampo({
  label,
  valor,
  base,
  rango,
  formato = 'cop',
  maxAbsoluto,
  onChange,
}: {
  label: string;
  valor: number;
  base: number;
  rango: number;
  formato?: 'cop' | 'num';
  maxAbsoluto?: number;
  onChange: (n: number) => void;
}) {
  const min = Math.max(0, Math.round(base * (1 - rango)));
  const baseMax = Math.round(base * (1 + rango));
  const max = Math.max(min + 1, maxAbsoluto !== undefined ? maxAbsoluto : baseMax);
  const step = Math.max(1, Math.round((max - min) / 100));
  const deltaPct = base > 0 ? (valor - base) / base : 0;
  const fmt = formato === 'num' ? (n: number) => num(n, 0) : (n: number) => cop(n);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <label className="text-[11px] uppercase tracking-wide text-slate-500">
          {label}
        </label>
        <span
          className={`text-[11px] ${
            deltaPct === 0
              ? 'text-slate-400'
              : deltaPct > 0
                ? 'text-emerald-700'
                : 'text-red-600'
          }`}
        >
          {deltaPct === 0 ? 'sin cambio' : `${deltaPct > 0 ? '+' : ''}${pct(deltaPct)}`}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={Math.min(max, Math.max(min, Math.round(valor)))}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full"
        />
        <input
          type="number"
          min={0}
          step={step}
          value={Math.round(valor)}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          className="w-24 input text-right text-sm"
        />
      </div>
      <div className="text-[11px] text-slate-500 mt-1">
        Base: {fmt(base)} · {fmt(valor)}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  strong,
  color,
}: {
  label: string;
  value: string;
  hint?: string;
  strong?: boolean;
  color?: string;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div
        className={`${strong ? 'text-xl sm:text-2xl' : 'text-base sm:text-lg'} font-semibold ${color ?? ''}`}
      >
        {value}
      </div>
      {hint && <div className="text-[11px] text-slate-500 leading-tight">{hint}</div>}
    </div>
  );
}
